import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import * as authApi from '@/api/auth'
import { useAuth } from '@/auth/AuthContext'
import { BrandMark } from '@/components/BrandMark'
import { useSiteSettings } from '@/siteSettingsContext'

export function ChangePassword() {
  const { user, loading, refreshUser, requireMfa, mfaEnabled } = useAuth()
  const { siteTitle, logoText, appVersion } = useSiteSettings()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-sky-50 to-slate-200 p-4">
        <p className="text-brand-muted">Loading…</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (!user.must_change_password) {
    if (requireMfa && !mfaEnabled) {
      return <Navigate to="/mfa/setup" replace />
    }
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.')
      return
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from the temporary password.')
      return
    }
    setSubmitting(true)
    try {
      await authApi.changePassword(currentPassword, newPassword)
      const updated = await refreshUser()
      if (requireMfa && !updated.totp_enabled) {
        navigate('/mfa/setup', { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    } catch (err) {
      const detail = err.response?.data?.detail
      setError(
        typeof detail === 'string'
          ? detail
          : 'Could not change password. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-sky-50 to-slate-200 p-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white/90 p-8 shadow-sm backdrop-blur">
        <BrandMark
          siteTitle={siteTitle}
          logoText={logoText}
          appVersion={appVersion}
          tone="light"
        />
        <p className="mt-3 text-sm text-brand-muted">
          You are using a temporary password. Choose a new password before continuing.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div>
            <label htmlFor="current" className="block text-sm font-medium text-brand">
              Temporary password
            </label>
            <input
              id="current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              minLength={8}
              disabled={submitting}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </div>
          <div>
            <label htmlFor="new" className="block text-sm font-medium text-brand">
              New password
            </label>
            <input
              id="new"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              disabled={submitting}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </div>
          <div>
            <label htmlFor="confirm" className="block text-sm font-medium text-brand">
              Confirm new password
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              disabled={submitting}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </div>

          {error && (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {submitting ? 'Saving…' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  )
}
